import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Train, MapPinned, Stamp, Compass, Shield } from "lucide-react";
import { api } from "../lib/api";
import Footer from "../components/Footer";
import LiveAmbience from "../components/LiveAmbience";
import SafeImage from "../components/SafeImage";

const HERO_IMG =
  "https://images.unsplash.com/photo-1677779709844-65f8cd68ddc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjB0cmFpbiUyMGpvdXJuZXklMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzc4NDkwMjYwfDA&ixlib=rb-4.1.0&q=85";

const MIDNIGHT_IMG = "https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=2000&q=80";

export default function Landing() {
  const [cities, setCities] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/featured-routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="pt-safe-navbar">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center animate-drift-slow"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/40" />
        <div className="absolute inset-0 bg-ink/30 mix-blend-multiply" />

        {/* Ambient life: embers, scan-line, live clock, departure ticker */}
        <LiveAmbience variant="hero" showTicker={true} embers={20} />

        <div className="absolute bottom-[18%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <Train className="absolute bottom-[18.5%] text-gold animate-train-slide h-6 w-6" style={{ left: 0 }} strokeWidth={2.5} />

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-6 pb-20 md:pb-24 pt-20 w-full">
          <div className="max-w-3xl animate-fade-up">
            <div className="iq-overline mb-3 md:mb-4 flex items-center gap-2">
              <Sparkles className="h-3 w-3" />
              An atlas for rail romantics · 21 curated cities
            </div>
            <h1 className="font-display text-cream text-[3.5rem] xs:text-6xl sm:text-7xl lg:text-[8rem] leading-[0.9] tracking-tight">
              CROSS<br/>
              <span className="text-gold">EUROPE</span><br/>
              <span className="h-serif text-cream/90 text-3xl sm:text-5xl lg:text-7xl normal-case font-normal tracking-normal">by overnight rail.</span>
            </h1>
            <p className="text-cream-dim text-base md:text-lg max-w-xl mt-5 md:mt-6 font-serif italic leading-relaxed">
              Eight hand-curated tours. Twenty-one cities pre-vetted for safety, ratings, and group bookings.
              Live timetables, a passport of stamps, a single invoice for your school trip.
            </p>
            <div className="flex flex-wrap gap-3 mt-7 md:mt-8">
              <Link to="/routes" data-testid="cta-tours" className="iq-btn-primary text-base">
                <Compass className="h-5 w-5" /> Browse the eight tours
              </Link>
              <Link to="/search" data-testid="cta-plan" className="iq-btn-secondary">
                Plan your own <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED TOURS — moved up to be the primary content */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="iq-overline mb-2">Hand-curated tours</div>
            <h2 className="font-display text-cream text-5xl md:text-6xl tracking-wider leading-none">Start with a story.</h2>
            <p className="font-serif italic text-cream-dim mt-3 max-w-xl text-lg">Eight grand journeys, written by rail romantics. Pick one — then bend it to your whim.</p>
          </div>
          <Link to="/routes" className="iq-btn-secondary text-sm py-2">All eight tours <ArrowRight className="h-4 w-4" /></Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {routes.slice(0, 4).map((r, i) => (
            <Link
              key={r.slug}
              to={`/routes/${r.slug}`}
              data-testid={`landing-route-${r.slug}`}
              className="group relative aspect-[3/4] overflow-hidden rounded-sm border border-panel-border animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <img src={r.hero} alt={r.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
              <div className="absolute top-3 left-3 bg-gold/90 text-ink text-[10px] font-display tracking-widest px-2 py-1 rounded-sm">{r.vibe} · {r.duration_days}d</div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-display text-cream text-2xl tracking-wider leading-tight line-clamp-2">{r.name}</h3>
                <p className="font-serif italic text-cream/80 mt-1 text-sm line-clamp-2">{r.tagline}</p>
                <div className="flex items-center gap-2 mt-3 text-[11px] text-cream-dim/80">
                  <span>{r.cities.length} cities</span>
                  <span>·</span>
                  <span className="text-gold font-display tracking-wider">from €{r.est_price_eur}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {routes.slice(4, 8).map((r, i) => (
            <Link
              key={r.slug}
              to={`/routes/${r.slug}`}
              data-testid={`landing-route-${r.slug}`}
              className="group relative aspect-[3/4] overflow-hidden rounded-sm border border-panel-border animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <img src={r.hero} alt={r.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
              <div className="absolute top-3 left-3 bg-gold/90 text-ink text-[10px] font-display tracking-widest px-2 py-1 rounded-sm">{r.vibe} · {r.duration_days}d</div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-display text-cream text-2xl tracking-wider leading-tight line-clamp-2">{r.name}</h3>
                <p className="font-serif italic text-cream/80 mt-1 text-sm line-clamp-2">{r.tagline}</p>
                <div className="flex items-center gap-2 mt-3 text-[11px] text-cream-dim/80">
                  <span>{r.cities.length} cities</span>
                  <span>·</span>
                  <span className="text-gold font-display tracking-wider">from €{r.est_price_eur}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CINEMATIC FULL-BLEED QUOTE */}
      <section
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-fixed bg-cover bg-center my-12"
        style={{ backgroundImage: `url(${MIDNIGHT_IMG})` }}
      >
        <div className="absolute inset-0 bg-ink/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-transparent to-ink" />
        <div className="relative text-center max-w-4xl px-6 py-24 animate-fade-up">
          <div className="iq-overline mb-6 flex items-center justify-center gap-2">
            <span className="h-px w-12 bg-gold/50" />
            <Sparkles className="h-3 w-3" />
            A manifesto
            <span className="h-px w-12 bg-gold/50" />
          </div>
          <p className="font-serif italic text-cream text-3xl md:text-5xl leading-[1.15] tracking-tight">
            "The point of the journey is not to arrive. The point is to wake up in a different country, with the smell of espresso on the platform and someone else's language in your ear."
          </p>
          <p className="font-display tracking-[0.3em] text-gold mt-8 text-sm">— INTERQUEST</p>
        </div>
      </section>

      {/* Why InterQuest trio */}
      <section className="max-w-[1400px] mx-auto px-6 py-24 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: Compass,
            title: "Curated, not aggregated",
            body: "Eight tours written by rail romantics. Twenty-one cities pre-vetted. No 200,000 hotel rows to sift through.",
          },
          {
            icon: Shield,
            title: "Safety Score v2",
            body: "Every property scored across six factors: location, security, solo-female, supervised minors, accessibility, hygiene.",
          },
          {
            icon: Stamp,
            title: "Stamps, XP, level up",
            body: "Save a route, earn XP. The more you ride, the more your passport fills. Made for the way you actually travel.",
          },
        ].map(({ icon: Icon, title, body }, i) => (
          <div key={title} className="iq-card p-8 animate-fade-up" style={{ animationDelay: `${i * 120}ms` }}>
            <Icon className="h-7 w-7 text-gold mb-5" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-cream tracking-wider mb-2">{title}</h3>
            <p className="text-cream-dim leading-relaxed text-sm">{body}</p>
          </div>
        ))}
      </section>

      {/* Cities — secondary now */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="iq-overline mb-2">Cities we serve</div>
            <h2 className="font-display text-cream text-4xl md:text-5xl tracking-wider">Twenty-one stops, hand-picked.</h2>
            <p className="font-serif italic text-cream-dim mt-2 text-sm">Each with verified accommodation, safety scores, and a real station.</p>
          </div>
          <Link to="/map" className="iq-btn-secondary text-sm py-2"><MapPinned className="h-4 w-4" /> Atlas <ArrowRight className="h-4 w-4" /></Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {cities.slice(0, 12).map((c, i) => (
            <Link
              key={c.slug}
              to={`/city/${c.slug}`}
              data-testid={`city-card-${c.slug}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-sm border border-panel-border animate-fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <SafeImage
                src={c.image}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="iq-overline mb-1">{c.country}</div>
                <div className="font-display text-cream text-2xl tracking-wider">{c.name}</div>
                <div className="text-cream-dim/80 text-[11px] italic font-serif mt-1 line-clamp-1">{c.tagline}</div>
              </div>
              <div className="absolute top-3 right-3 bg-gold/90 text-ink text-[10px] font-display tracking-widest px-2 py-1 rounded-sm">
                +{c.xp} XP
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Groups + Schools CTA */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24">
        <div className="iq-card p-8 md:p-12 grid md:grid-cols-[1fr_auto_auto] gap-4 md:gap-6 items-center bg-gradient-to-br from-panel via-panel to-ink">
          <div>
            <div className="iq-overline mb-2"><Sparkles className="inline h-3 w-3 mr-1" /> For schools, teams &amp; tribes</div>
            <h2 className="font-display text-cream text-4xl md:text-5xl tracking-wider mb-3">Move ten. Move five hundred.</h2>
            <p className="font-serif italic text-cream-dim text-lg max-w-2xl leading-relaxed">
              We negotiate dorm blocks, supervised minors with verified safety scores, accessibility,
              and packed-lunch logistics for groups of 10–500. One contact, one invoice.
            </p>
          </div>
          <Link to="/schools" data-testid="cta-schools" className="iq-btn-secondary text-base">
            For schools <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/groups" className="iq-btn-primary text-base">
            Group quote <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
